import { useId, useMemo, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { denialReason } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import { cx } from '@/lib/cx';
import { ROLE_LABELS, roleLabel } from '@/lib/roles';
import type { User, UserCreateInput, UserRole, UserUpdateInput } from '@/lib/types';
import {
  changedGroupIds,
  groupMembershipOptions,
  groupsOf,
  refusedGroupIds,
  useUserGroups,
} from '@/lib/user-groups';
import {
  Button,
  Combobox,
  type ComboboxOption,
  ConfirmDialog,
  Field,
  Input,
  Modal,
  MultiCombobox,
  Select,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

export interface UserFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Omitted = create mode. Provided = edit mode. */
  user?: User;
  /** The signed-in account's id, so the self-edit guards below can apply. */
  currentUserId?: number | null;
  /**
   * The saved record, as the server returned it.
   *
   * ⚠️ **On a CREATE the argument is load-bearing and is not merely a
   * notification**: `UsersPage` hands it straight to `OnboardingEmailModal`, so
   * the admin composes the welcome message for the account they have just made
   * without going back to find its row. A caller that ignores it still works —
   * the row action on `/users` reaches the same composer — but the create flow
   * then ends on a temporary password nobody has been told.
   *
   * ⚠️ **It hands over the record, never the PASSWORD.** The typed password
   * lives only in this modal's state and is cleared when it closes; the
   * onboarding composer asks for it again, deliberately, because that request
   * mails it to a person and must be a separate decision with the value in
   * front of whoever makes it.
   */
  onSaved: (user: User) => void;
}

/* -------------------------------------------------------------------------
 * Contract
 *
 * POST /users   — email, name, password (>= 12) required; role defaults to
 *                 `basic_user`; designation optional and nullable.
 * PATCH /users/:id — every key optional but at least one required ({} is a 422),
 *                 designation accepts null to clear.
 *
 * Both are admin-tier only, and a duplicate email is a **409** — which carries
 * no `error.details`, so `ApiError.fieldErrors` is empty and the message has to
 * be pinned to the email field by hand. See `handleError`.
 * --------------------------------------------------------------------- */

const NAME_MAX = 120;
const EMAIL_MAX = 255;
const DESIGNATION_MAX = 120;
/*
 * 🔴 PASSWORD_MIN LOWERED FROM 12 TO 8 — owner's instruction, 2026-09-04, for a
 * bulk reset of every account to a shared handout password.
 *
 * ⚠️ THERE IS A SECOND COPY, in `src/pages/ResetPasswordPage.tsx`, and the two
 * must move together — that file says so at its own declaration. A client bound
 * left at 12 while the server accepts 8 does not error: the form simply refuses
 * a password the API would have taken, and reports it as the user's mistake.
 *
 * ⚠️ AND NEITHER COPY IS THE RULE. The server is
 * (`users.schema.ts`, `auth.schema.ts`); these exist only to say so before a
 * round trip. If they disagree, the server wins and this message is a lie.
 */
const PASSWORD_MIN = 8;
const PASSWORD_MAX = 200;

/*
 * Derived from ROLE_LABELS rather than re-listing the five slugs, so a role
 * added there appears in this picker instead of silently going missing. The
 * object literal's order is the tier order, which is the order to offer.
 */
const ROLE_OPTIONS = Object.keys(ROLE_LABELS) as UserRole[];

/** The server's default when `role` is omitted — so the form shows the truth. */
const DEFAULT_ROLE: UserRole = 'basic_user';

/* Deliberately loose: the server owns the real check, and a stricter pattern
 * here would reject addresses the API accepts. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* -------------------------------------------------------------------------
 * Timezones
 *
 * A hand-curated list of IANA Area/Location zones. Both of the obvious ways to
 * build one are wrong here, and both fail by an hour rather than loudly:
 *
 * - **Not `Intl.supportedValuesOf('timeZone')`.** It does not contain
 *   `Asia/Kolkata` — only the legacy `Asia/Calcutta` alias — and Asia/Kolkata is
 *   the column default that every account currently holds. A picker built from
 *   that list could not display the zone eleven of eleven users are on.
 * - **Never abbreviations.** `EST` resolves to America/Panama, which has no DST,
 *   so a New York user stored as `EST` is an hour out for eight months a year.
 *   Area/Location names only — the server accepts either, so nothing would
 *   complain.
 *
 * The list is a working subset covering where this business operates, not all
 * ~600 zones — `timezoneOptions` below handles a stored value outside it. Order
 * carries the grouping: `Combobox` has no optgroups, so each row’s region and
 * cities go on the hint line, where the search box reads them too (“Mumbai”
 * finds Asia/Kolkata). India leads because it is the default and where most of
 * the staff are.
 * --------------------------------------------------------------------- */
interface TimezoneChoice {
  /** The IANA name. Sent verbatim; this string is the stored value. */
  zone: string;
  /** Group heading. Searchable. */
  region: string;
  /** Cities a reader recognises without knowing IANA names. Searchable. */
  places: string;
}

const TIMEZONES: TimezoneChoice[] = [
  { zone: 'Asia/Kolkata', region: 'India', places: 'Mumbai, Delhi, Bengaluru, Chennai' },

  { zone: 'Europe/London', region: 'UK & Ireland', places: 'London, Manchester, Edinburgh' },
  { zone: 'Europe/Dublin', region: 'UK & Ireland', places: 'Dublin, Cork' },

  { zone: 'Europe/Lisbon', region: 'Europe', places: 'Lisbon, Porto' },
  { zone: 'Europe/Madrid', region: 'Europe', places: 'Madrid, Barcelona' },
  { zone: 'Europe/Paris', region: 'Europe', places: 'Paris, Lyon' },
  { zone: 'Europe/Brussels', region: 'Europe', places: 'Brussels, Antwerp' },
  { zone: 'Europe/Amsterdam', region: 'Europe', places: 'Amsterdam, Rotterdam' },
  { zone: 'Europe/Berlin', region: 'Europe', places: 'Berlin, Munich, Frankfurt' },
  { zone: 'Europe/Zurich', region: 'Europe', places: 'Zurich, Geneva, Basel' },
  { zone: 'Europe/Rome', region: 'Europe', places: 'Rome, Milan' },
  { zone: 'Europe/Vienna', region: 'Europe', places: 'Vienna, Salzburg' },
  { zone: 'Europe/Copenhagen', region: 'Europe', places: 'Copenhagen' },
  { zone: 'Europe/Oslo', region: 'Europe', places: 'Oslo' },
  { zone: 'Europe/Stockholm', region: 'Europe', places: 'Stockholm' },
  { zone: 'Europe/Helsinki', region: 'Europe', places: 'Helsinki' },
  { zone: 'Europe/Prague', region: 'Europe', places: 'Prague' },
  { zone: 'Europe/Warsaw', region: 'Europe', places: 'Warsaw, Krakow' },
  { zone: 'Europe/Athens', region: 'Europe', places: 'Athens, Thessaloniki' },
  { zone: 'Europe/Istanbul', region: 'Europe', places: 'Istanbul, Ankara' },
  { zone: 'Europe/Moscow', region: 'Europe', places: 'Moscow, St Petersburg' },

  { zone: 'America/St_Johns', region: 'North America', places: 'St John’s, Newfoundland' },
  { zone: 'America/Halifax', region: 'North America', places: 'Halifax, Moncton' },
  { zone: 'America/New_York', region: 'North America', places: 'New York, Boston, Washington' },
  { zone: 'America/Toronto', region: 'North America', places: 'Toronto, Ottawa, Montreal' },
  { zone: 'America/Chicago', region: 'North America', places: 'Chicago, Dallas, Houston' },
  { zone: 'America/Mexico_City', region: 'North America', places: 'Mexico City, Guadalajara' },
  { zone: 'America/Denver', region: 'North America', places: 'Denver, Salt Lake City' },
  { zone: 'America/Phoenix', region: 'North America', places: 'Phoenix, Tucson' },
  { zone: 'America/Los_Angeles', region: 'North America', places: 'Los Angeles, San Francisco' },
  { zone: 'America/Vancouver', region: 'North America', places: 'Vancouver, Victoria' },
  { zone: 'America/Anchorage', region: 'North America', places: 'Anchorage' },
  { zone: 'Pacific/Honolulu', region: 'North America', places: 'Honolulu' },

  { zone: 'Asia/Dubai', region: 'The Gulf', places: 'Dubai, Abu Dhabi, Sharjah' },
  { zone: 'Asia/Muscat', region: 'The Gulf', places: 'Muscat' },
  { zone: 'Asia/Qatar', region: 'The Gulf', places: 'Doha' },
  { zone: 'Asia/Bahrain', region: 'The Gulf', places: 'Manama' },
  { zone: 'Asia/Kuwait', region: 'The Gulf', places: 'Kuwait City' },
  { zone: 'Asia/Riyadh', region: 'The Gulf', places: 'Riyadh, Jeddah, Dammam' },

  { zone: 'Asia/Karachi', region: 'South & Southeast Asia', places: 'Karachi, Lahore, Islamabad' },
  { zone: 'Asia/Colombo', region: 'South & Southeast Asia', places: 'Colombo' },
  { zone: 'Asia/Kathmandu', region: 'South & Southeast Asia', places: 'Kathmandu' },
  { zone: 'Asia/Dhaka', region: 'South & Southeast Asia', places: 'Dhaka' },
  { zone: 'Asia/Bangkok', region: 'South & Southeast Asia', places: 'Bangkok, Hanoi' },
  { zone: 'Asia/Jakarta', region: 'South & Southeast Asia', places: 'Jakarta' },
  { zone: 'Asia/Kuala_Lumpur', region: 'South & Southeast Asia', places: 'Kuala Lumpur' },
  { zone: 'Asia/Singapore', region: 'South & Southeast Asia', places: 'Singapore' },
  { zone: 'Asia/Manila', region: 'South & Southeast Asia', places: 'Manila' },

  { zone: 'Asia/Hong_Kong', region: 'East Asia', places: 'Hong Kong' },
  { zone: 'Asia/Shanghai', region: 'East Asia', places: 'Shanghai, Beijing, Shenzhen' },
  { zone: 'Asia/Taipei', region: 'East Asia', places: 'Taipei' },
  { zone: 'Asia/Seoul', region: 'East Asia', places: 'Seoul' },
  { zone: 'Asia/Tokyo', region: 'East Asia', places: 'Tokyo, Osaka' },

  { zone: 'Africa/Accra', region: 'Africa', places: 'Accra, Kumasi' },
  { zone: 'Africa/Lagos', region: 'Africa', places: 'Lagos, Abuja' },
  { zone: 'Africa/Cairo', region: 'Africa', places: 'Cairo' },
  { zone: 'Africa/Nairobi', region: 'Africa', places: 'Nairobi' },
  { zone: 'Africa/Johannesburg', region: 'Africa', places: 'Johannesburg, Cape Town' },

  { zone: 'Australia/Perth', region: 'Australia & New Zealand', places: 'Perth' },
  { zone: 'Australia/Adelaide', region: 'Australia & New Zealand', places: 'Adelaide' },
  { zone: 'Australia/Brisbane', region: 'Australia & New Zealand', places: 'Brisbane' },
  { zone: 'Australia/Sydney', region: 'Australia & New Zealand', places: 'Sydney, Canberra' },
  { zone: 'Australia/Melbourne', region: 'Australia & New Zealand', places: 'Melbourne' },
  { zone: 'Pacific/Auckland', region: 'Australia & New Zealand', places: 'Auckland, Wellington' },

  { zone: 'UTC', region: 'Coordinated Universal Time', places: 'No offset, no daylight saving' },
];

/** The server's default when `timezone` is omitted — so the form shows the truth. */
const DEFAULT_TIMEZONE = 'Asia/Kolkata';

/**
 * The zone’s offset right now, as '+05:30'.
 *
 * Computed at render rather than written into the table above: most of these
 * zones move twice a year, so a baked-in offset is wrong for months at a time —
 * and the offset is the entire reason someone who does not know IANA names can
 * find their row.
 *
 * `shortOffset` yields 'GMT+5:30', 'GMT-4' or a bare 'GMT'; all three are
 * normalised to a fixed-width signed offset. A name the runtime cannot format
 * throws, and returns null so the label falls back to the bare zone rather than
 * taking the form down.
 */
function currentOffset(zone: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    const name = parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
    const match = /^GMT(?:([+-])(\d{1,2})(?::(\d{2}))?)?$/.exec(name);
    if (!match) return null;
    const [, sign = '+', hours = '0', minutes = '00'] = match;
    return `${sign}${hours.padStart(2, '0')}:${minutes}`;
  } catch {
    return null;
  }
}

/** 'Asia/Kolkata' -> 'Asia/Kolkata (+05:30)'. */
function zoneLabel(zone: string): string {
  const offset = currentOffset(zone);
  return offset ? `${zone} (${offset})` : zone;
}

/**
 * The picker’s options, with the record’s own zone injected when the curated
 * list does not hold it.
 *
 * Same rule — and the same reasoning — as `withStoredOption()` in
 * `@/lib/use-reference-options`: **a stored value that is not on offer must
 * still show as selected.** `timezone` is NOT NULL and the PATCH sends only what
 * changed, so a control that blanked or silently reset would misreport the
 * account’s real zone on screen while saving nothing — and the next admin to
 * change an unrelated field would be reading a zone the user is not on. This
 * list is a deliberate subset of ~600 IANA names, so an outside value is an
 * expected state rather than a fault, and is labelled as kept rather than
 * flagged as wrong.
 */
/** Stable identity for the closed state, so it is not a new array each render. */
const NO_ZONE_OPTIONS: ComboboxOption[] = [];

function timezoneOptions(stored: string | null | undefined): ComboboxOption[] {
  const options: ComboboxOption[] = TIMEZONES.map((choice) => ({
    value: choice.zone,
    label: zoneLabel(choice.zone),
    hint: `${choice.region} · ${choice.places}`,
  }));

  const zone = stored?.trim() ?? '';
  if (!zone || TIMEZONES.some((choice) => choice.zone === zone)) return options;

  return [
    { value: zone, label: zoneLabel(zone), hint: 'Set outside this list — kept as it is' },
    ...options,
  ];
}

/*
 * Reporting currency (owner 2026-10-28): the currency this person's dashboards
 * open in. Nullable — '' in form state is "None" and is sent as null. Offered on
 * EDIT only: `PATCH /users/:id` takes the key, POST is not contracted to, and a
 * strict create schema would refuse an unknown key outright. A record without
 * the key (`undefined`) comes from an older API, so the control is not shown and
 * nothing is sent.
 */
const REPORTING_CURRENCIES = ['USD', 'EUR', 'GBP'] as const;
type ReportingCurrency = (typeof REPORTING_CURRENCIES)[number];

function isReportingCurrency(value: string): value is ReportingCurrency {
  return (REPORTING_CURRENCIES as readonly string[]).includes(value);
}

/** The offered codes, plus a stored code outside them so it still shows as selected. */
function reportingCurrencyOptions(stored: string | null | undefined): string[] {
  const code = stored?.trim() ?? '';
  const offered: string[] = [...REPORTING_CURRENCIES];
  return code && !offered.includes(code) ? [code, ...offered] : offered;
}

/**
 * Field names the API can name in a 422, and this form can render against.
 *
 * ⚠️ `groupIds` is in here because the membership editor below renders it. A
 * key left OUT of this set is routed to `formError` instead — which is right for
 * a key no control is bound to, and wrong for one that is: the banner would read
 * "groupIds: …" while the control it belongs to sat unmarked.
 */
const KNOWN_FIELDS = new Set([
  'email',
  'name',
  'password',
  'role',
  'designation',
  'timezone',
  'reportingCurrency',
  'groupIds',
]);

interface FormState {
  name: string;
  email: string;
  designation: string;
  role: UserRole;
  timezone: string;
  /** ISO code, or '' for None (sent as null). */
  reportingCurrency: string;
  /**
   * User-group membership as id strings — the multi-select's own value type.
   *
   * ⚠️ **Never assigned into a payload directly.** `groupIds` is a whole-set
   * REPLACE where `[]` CLEARS and an absent key leaves membership alone, so the
   * only thing allowed to turn this into a payload value is `changedGroupIds`,
   * which returns `null` for "unchanged" — see `buildPatch`.
   */
  groupIds: string[];
}

function blankForm(): FormState {
  return {
    name: '',
    email: '',
    designation: '',
    role: DEFAULT_ROLE,
    timezone: DEFAULT_TIMEZONE,
    reportingCurrency: '',
    groupIds: [],
  };
}

/** The saved record as form values — the baseline every PATCH diff is taken against. */
function recordForm(user: User | undefined): FormState {
  if (!user) return blankForm();
  return {
    name: user.name,
    email: user.email,
    designation: user.designation ?? '',
    role: user.role,
    // NOT NULL server-side, but a missing value must not blank the control —
    // falling back to the column default keeps form and baseline equal, so
    // nothing is sent for a field nobody touched.
    timezone: user.timezone?.trim() || DEFAULT_TIMEZONE,
    // null (None) and undefined (older API) both baseline to '', so an
    // untouched control never produces a diff.
    reportingCurrency: user.reportingCurrency?.trim() ?? '',
    /*
     * Read through `groupsOf` rather than off the record: the key is contracted
     * as always-present, but a response from a server that predates it would
     * throw here and take the modal down. `[]` is the same baseline as "in no
     * group", so an absent key sends nothing — which is correct against a server
     * that does not know the field.
     */
    groupIds: groupsOf(user).map((group) => String(group.id)),
  };
}

/**
 * POST body. `password` is required by the API, so it is a plain required field
 * here rather than the opt-in disclosure the edit form uses — there is no
 * existing account to reset, and nothing to do by accident.
 */
function buildCreate(form: FormState, password: string): UserCreateInput {
  const body: UserCreateInput = {
    email: form.email.trim(),
    name: form.name.trim(),
    password,
    // Sent explicitly rather than relying on the server default: the Select
    // shows a value, and saving something other than what is on screen is a bug.
    role: form.role,
    // Same reason, and it matters more here: the server defaults to
    // Asia/Kolkata, so an omitted zone quietly puts a London hire on Indian
    // time — and their task due times would look right while being wrong.
    timezone: form.timezone.trim(),
  };
  const designation = form.designation.trim();
  if (designation) body.designation = designation;
  /*
   * Membership, only when somebody picked one. On create there is no stored set
   * to preserve, so `[]` and absent mean the same thing — and the app-wide rule
   * is to send nothing the server does not need: an empty array here would be a
   * write nobody asked for. `changedGroupIds` is still what builds the value, so
   * there remains exactly one place that can produce a `groupIds`.
   */
  const groupIds = changedGroupIds(form.groupIds, []);
  if (groupIds !== null && groupIds.length > 0) body.groupIds = groupIds;
  return body;
}

/**
 * PATCH body: the diff, and nothing else.
 *
 * An omitted key is untouched and an explicit null clears the column, so
 * spreading the loaded record back would rewrite fields nobody opened. `name`
 * and `email` are non-null columns, so blanking either is treated as no change
 * (validation rejects it first); `designation` maps "" to null so it can be
 * cleared.
 *
 * `password` is only ever present when the admin opened the reset disclosure
 * and typed one — it has no baseline and is never part of an ordinary diff.
 */
function buildPatch(
  form: FormState,
  baseline: FormState,
  password: string,
  roleEditable: boolean,
): UserUpdateInput {
  const body: UserUpdateInput = {};

  const name = form.name.trim();
  if (name && name !== baseline.name.trim()) body.name = name;

  // Compared case-insensitively because the server lowercases on write: retyping
  // the same address in a different case is not a change worth sending.
  const email = form.email.trim();
  if (email && email.toLowerCase() !== baseline.email.trim().toLowerCase()) body.email = email;

  const designation = form.designation.trim();
  if (designation !== baseline.designation.trim()) body.designation = designation || null;

  // A NOT NULL column, like `name` and `email` above: an empty value counts as
  // no change rather than being sent, because there is no such thing as no zone.
  const timezone = form.timezone.trim();
  if (timezone && timezone !== baseline.timezone.trim()) body.timezone = timezone;

  // Nullable: '' (None) clears the column.
  // A kept out-of-list stored code can only be re-selected, never sent.
  if (form.reportingCurrency !== baseline.reportingCurrency) {
    if (form.reportingCurrency === '') body.reportingCurrency = null;
    else if (isReportingCurrency(form.reportingCurrency)) {
      body.reportingCurrency = form.reportingCurrency;
    }
  }

  // Belt and braces: the control is disabled when editing your own account, so
  // this can't differ — but a demotion you can't undo deserves two guards.
  if (roleEditable && form.role !== baseline.role) body.role = form.role;

  /*
   * ⚠️ **The `[]`-versus-absent rule, made structural.** `groupIds` is a
   * whole-set REPLACE: `[]` clears every membership, an absent key leaves it
   * alone. `changedGroupIds` returns `null` for "unchanged" — a value a payload
   * cannot express — and it is the ONLY thing in this file that produces a
   * `groupIds` value. So an empty array can be reached only by somebody
   * removing the last chip from a set that had one, which is the single case
   * where clearing is what was asked for. There is no second assignment to add
   * a `?? []` to.
   */
  const groupIds = changedGroupIds(form.groupIds, baseline.groupIds);
  if (groupIds !== null) body.groupIds = groupIds;

  if (password) body.password = password;

  return body;
}

/**
 * Client-side validation, mirroring the server and going no further. Passwords
 * are never trimmed — leading and trailing spaces are real characters, and
 * trimming would silently save something other than what was typed.
 */
function validate(
  form: FormState,
  password: string,
  passwordRequired: boolean,
  passwordStaged: boolean,
): Record<string, string> {
  const errors: Record<string, string> = {};

  const name = form.name.trim();
  if (!name) errors.name = 'Enter the person’s name.';
  else if (name.length > NAME_MAX) errors.name = `Name must be ${NAME_MAX} characters or fewer.`;

  const email = form.email.trim();
  if (!email) errors.email = 'Enter an email address.';
  else if (!EMAIL_PATTERN.test(email)) errors.email = 'Enter a valid email address.';
  else if (email.length > EMAIL_MAX) {
    errors.email = `Email must be ${EMAIL_MAX} characters or fewer.`;
  }

  if (form.designation.trim().length > DESIGNATION_MAX) {
    errors.designation = `Designation must be ${DESIGNATION_MAX} characters or fewer.`;
  }

  /*
   * Mirrors the server and goes no further: it validates structurally — a '/'
   * in the name, or exactly 'UTC'. Testing membership of TIMEZONES instead would
   * reject the very out-of-list value the picker deliberately keeps.
   */
  const timezone = form.timezone.trim();
  if (!timezone) errors.timezone = 'Choose a timezone.';
  else if (!timezone.includes('/') && timezone !== 'UTC') {
    errors.timezone =
      'Choose an Area/Location zone such as Asia/Kolkata. Abbreviations like EST are not accepted — they lose daylight saving.';
  }

  if (passwordRequired && !password) {
    errors.password = `Choose a password of at least ${PASSWORD_MIN} characters.`;
  } else if ((passwordRequired || passwordStaged) && password) {
    if (password.length < PASSWORD_MIN) {
      errors.password = `Password must be at least ${PASSWORD_MIN} characters — that one is ${password.length}.`;
    } else if (password.length > PASSWORD_MAX) {
      errors.password = `Password must be ${PASSWORD_MAX} characters or fewer.`;
    }
  }

  return errors;
}

export function UserFormModal({
  open,
  onClose,
  user,
  currentUserId,
  onSaved,
}: UserFormModalProps) {
  const isEdit = user !== undefined;
  const isSelf = isEdit && user.id === currentUserId;
  /*
   * An admin who demotes themselves loses admin access the moment it saves, and
   * nothing in the UI can restore it — only another admin can. So the control is
   * disabled rather than silently dropped, with the reason next to it.
   */
  /*
   * 2026-10-02 (owner): changing an existing person's role and resetting their
   * password are `users.manage-access`, not `users.update`. Choosing the role
   * of a NEW account is part of creating it and stays with `users.create`.
   */
  const { ability } = useAuth();
  const canManageAccess = ability.canManageAccess();
  const accessDenial = isEdit && !canManageAccess
    ? denialReason(ability, 'manage-access', 'users')
    : null;
  const roleEditable = !isSelf && (!isEdit || canManageAccess);

  // Identity of the loaded record: changes when a different record — or a newer
  // version of the same one — arrives, not merely when the parent re-renders.
  const userKey = user ? `${user.id}:${user.updatedAt}` : 'new';
  const formId = useId();

  const [form, setForm] = useState<FormState>(() => recordForm(user));
  const [baseline, setBaseline] = useState<FormState>(() => recordForm(user));
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  /** Edit mode only: whether the admin has deliberately opened the reset section. */
  const [resetOpen, setResetOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** Non-null while the reset confirmation is on screen; holds the body to send. */
  const [pendingReset, setPendingReset] = useState<UserUpdateInput | null>(null);
  /**
   * The group ids the server named in a 422.
   *
   * The refusal lists EVERY offender in one message — "Unknown or inactive user
   * group(s): 4, 9" — so all the bad chips are marked in one pass rather than
   * the admin resubmitting to discover the next one.
   */
  const [refusedGroups, setRefusedGroups] = useState<string[]>([]);

  /*
   * The group master. Read only while the modal is open — `Modal` renders
   * nothing while closed, so there is nothing to fill until then.
   *
   * ⚠️ *SUPERSEDED: "and the rate limit is 120/60s per IP shared across the
   * office."* It is **12,000/60s** (corrected 2026-09-17). Gating on `open` is
   * still right — it is a request nothing on screen can use — but the
   * arithmetic that reason cited is off by a factor of a hundred.
   */
  const groups = useUserGroups(open);

  /*
   * Reopening must not show the previous record's values, the last attempt's
   * errors, or — most importantly — a password left in state. Done as a
   * render-phase reset rather than an effect, which would paint the stale form
   * for a frame first.
   */
  const resetKey = `${open ? 'open' : 'closed'}|${userKey}`;
  const [appliedKey, setAppliedKey] = useState(resetKey);
  if (appliedKey !== resetKey) {
    setAppliedKey(resetKey);
    setForm(recordForm(user));
    setBaseline(recordForm(user));
    setPassword('');
    setShowPassword(false);
    setResetOpen(false);
    setErrors({});
    setFormError(null);
    setSaving(false);
    setPendingReset(null);
    setRefusedGroups([]);
  }

  // A password only counts as staged while the section is open: closing it
  // clears the value as well, so nothing can be sent that isn't on screen.
  const passwordStaged = isEdit && resetOpen && password !== '';

  // Derived from the payload itself, so "Save is enabled" and "we have something
  // to send" can never disagree — a PATCH {} is a 422.
  const patch = useMemo(
    () =>
      isEdit
        ? buildPatch(form, baseline, resetOpen ? password : '', roleEditable)
        : null,
    [isEdit, form, baseline, resetOpen, password, roleEditable],
  );
  const dirty = patch === null || Object.keys(patch).length > 0;

  /*
   * Built only while the modal is open, and rebuilt each time it opens: every
   * label carries a live UTC offset, and half these zones move at a DST
   * boundary, so the list is not something to compute once and keep. `Modal`
   * renders nothing while closed, so there is nothing to feed until then.
   */
  const zoneOptions = useMemo(
    () => (open ? timezoneOptions(user?.timezone) : NO_ZONE_OPTIONS),
    [open, user?.timezone],
  );

  const currencyOptions = useMemo(
    () => reportingCurrencyOptions(user?.reportingCurrency),
    [user?.reportingCurrency],
  );

  /*
   * The membership options: every active group, plus any this account already
   * holds that the master no longer offers.
   *
   * The injection is the same defence `regionOptions` makes for a company's
   * regions, and for the same reason: `groupIds` is a whole-set REPLACE, so a
   * held membership with no chip to sit on would be dropped by the next save of
   * an unrelated field — a 200, with nothing on screen having said so.
   */
  const groupOptions = useMemo(
    () => groupMembershipOptions(groups.records, user ? groupsOf(user) : []),
    [groups.records, user],
  );

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((previous) => ({ ...previous, [key]: value }));
    // A refusal about a set the admin has since edited is stale; the chips it
    // marked may not even be there any more.
    if (key === 'groupIds') setRefusedGroups([]);
    setErrors((previous) => {
      if (!(key in previous)) return previous;
      const next = { ...previous };
      delete next[key as string];
      return next;
    });
  }

  function toggleReset(next: boolean) {
    setResetOpen(next);
    if (!next) {
      // Closing discards the typed password rather than keeping it armed behind
      // a collapsed section.
      setPassword('');
      setShowPassword(false);
      setErrors((previous) => {
        if (!('password' in previous)) return previous;
        const rest = { ...previous };
        delete rest.password;
        return rest;
      });
    }
  }

  function handleError(error: unknown, sentGroupIds: readonly string[]) {
    if (!(error instanceof ApiError)) {
      setFormError('Could not reach the server. Check your connection and try again.');
      return;
    }

    // Marked whatever else the refusal turns out to be about: the ids come from
    // the message body, and intersecting with what was sent keeps a stray number
    // in the prose from reddening a chip that was never in question.
    setRefusedGroups(sentGroupIds.length > 0 ? refusedGroupIds(error, sentGroupIds) : []);

    const mapped: Record<string, string> = {};
    /*
     * Seeded with the messages that belong to no field. A `.strict()` rejection
     * names the offending key with an EMPTY path, so it can only ever be shown
     * at form level — keyed by path it would render nowhere.
     */
    const unmapped: string[] = [...error.formMessages];
    for (const [path, message] of Object.entries(error.fieldErrors)) {
      if (KNOWN_FIELDS.has(path)) mapped[path] = message;
      else unmapped.push(`${path}: ${message}`);
    }

    /*
     * A duplicate email is a 409 with no `details`, so nothing above catches it.
     * Pinning the message to the email field is the difference between "the
     * request failed" and "change this address".
     */
    if (error.status === 409 && Object.keys(mapped).length === 0) {
      setErrors({ email: 'That email address already belongs to another SEED EMERGE account.' });
      setFormError(null);
      return;
    }

    setErrors(mapped);
    if (unmapped.length > 0) setFormError(`${error.message} — ${unmapped.join('; ')}`);
    else if (Object.keys(mapped).length > 0) setFormError(null);
    else if (error.status === 403) {
      setFormError(
        isEdit
          ? 'Only administrators can edit user accounts. Your access may have changed since this page loaded.'
          : 'Only administrators can add users. Your access may have changed since this page loaded.',
      );
    } else if (error.status === 404) setFormError('This user no longer exists.');
    else setFormError(error.message);
  }

  async function submit(body: UserCreateInput | UserUpdateInput) {
    setSaving(true);
    setErrors({});
    setFormError(null);
    setRefusedGroups([]);
    // What this request actually claims about membership. `[]` when the key is
    // absent, which is the state where no chip may be marked at all.
    const sentGroupIds = (body.groupIds ?? []).map(String);
    try {
      const result = user
        ? await api.updateUser(user.id, body as UserUpdateInput)
        : await api.createUser(body as UserCreateInput);
      onSaved(result.data);
      onClose();
    } catch (error) {
      handleError(error, sentGroupIds);
    } finally {
      setSaving(false);
      setPendingReset(null);
    }
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;

    const clientErrors = validate(form, password, !isEdit, passwordStaged);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      setFormError(null);
      return;
    }

    const body = patch ?? buildCreate(form, password);
    // Belt and braces: Save is disabled in this state, but an empty patch is a
    // guaranteed 422 and must never leave the client.
    if (isEdit && Object.keys(body).length === 0) return;

    // A password reset takes effect immediately, signs the person out everywhere
    // and emails them (since 2026-09-24; it used to tell nobody), so it gets a
    // second, explicit decision rather than riding along with a save.
    if (isEdit && 'password' in body && body.password) {
      setPendingReset(body as UserUpdateInput);
      return;
    }

    void submit(body);
  }

  const heading = isEdit ? `Edit ${user.name}` : 'Invite user';

  /**
   * The password control. Rendered through `Field`'s render prop in both modes,
   * so the label, hint and error wiring stay in one place while the input itself
   * — including the reveal toggle — is written once.
   */
  const passwordInput = ({
    id,
    'aria-describedby': describedBy,
  }: {
    id: string;
    'aria-describedby': string | undefined;
  }) => (
    <Input
      id={id}
      aria-describedby={describedBy}
      name="password"
      type={showPassword ? 'text' : 'password'}
      value={password}
      maxLength={PASSWORD_MAX}
      // Never `current-password`: that offers the signed-in admin's own
      // credentials for somebody else's account.
      autoComplete="new-password"
      placeholder={`At least ${PASSWORD_MIN} characters`}
      invalid={Boolean(errors.password)}
      onChange={(event) => {
        setPassword(event.target.value);
        setErrors((previous) => {
          if (!('password' in previous)) return previous;
          const rest = { ...previous };
          delete rest.password;
          return rest;
        });
      }}
      trailingSlot={
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={() => setShowPassword((visible) => !visible)}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          title={showPassword ? 'Hide password' : 'Show password'}
          leadingIcon={showPassword ? <EyeOffIcon /> : <EyeIcon />}
        />
      }
    />
  );

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        size="md"
        dismissOnBackdrop={false}
        title={heading}
        footer={
          <>
            {isEdit && !dirty && (
              <p className="text-xs text-fg-muted sm:mr-auto sm:self-center">No changes yet.</p>
            )}
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              form={formId}
              loading={saving}
              disabled={isEdit && !dirty}
            >
              {isEdit ? (passwordStaged ? 'Save and reset password' : 'Save changes') : 'Add user'}
            </Button>
          </>
        }
      >
        <form id={formId} noValidate onSubmit={handleSubmit} className="grid gap-3">
          {formError && (
            <Notice tone="danger" role="alert" className="break-words">
              {formError}
            </Notice>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Full name" required error={errors.name}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  name="name"
                  value={form.name}
                  maxLength={NAME_MAX}
                  autoComplete="off"
                  invalid={Boolean(errors.name)}
                  onChange={(event) => setField('name', event.target.value)}
                />
              )}
            </Field>

            <Field label="Email" required error={errors.email}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  name="email"
                  type="email"
                  value={form.email}
                  maxLength={EMAIL_MAX}
                  // Not `email`: the browser would offer the signed-in admin's
                  // own address for someone else's account.
                  autoComplete="off"
                  invalid={Boolean(errors.email)}
                  onChange={(event) => setField('email', event.target.value)}
                />
              )}
            </Field>

            <Field
              label="Role"
              error={errors.role}
              // Kept deliberately: this is why the control is disabled, not a
              // hint about what roles mean.
              hint={
                isSelf ? 'You cannot change your own role — ask another admin.' : undefined
              }
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <span className="block" {...(!isSelf && accessDenial ? { title: accessDenial } : {})}>
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  name="role"
                  value={form.role}
                  disabled={!roleEditable}
                  invalid={Boolean(errors.role)}
                  onChange={(event) => setField('role', event.target.value as UserRole)}
                >
                  {ROLE_OPTIONS.map((role) => (
                    <option key={role} value={role}>
                      {roleLabel(role)}
                    </option>
                  ))}
                </Select>
                </span>
              )}
            </Field>

            <Field label="Designation" error={errors.designation}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  name="designation"
                  value={form.designation}
                  maxLength={DESIGNATION_MAX}
                  autoComplete="off"
                  invalid={Boolean(errors.designation)}
                  onChange={(event) => setField('designation', event.target.value)}
                />
              )}
            </Field>

            <div className="sm:col-span-2">
              <Field label="Timezone" error={errors.timezone}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Combobox
                    id={id}
                    aria-describedby={describedBy}
                    value={form.timezone}
                    options={zoneOptions}
                    // No empty option: the column is NOT NULL, so “no zone” is
                    // not a state this field can express.
                    placeholder="Select a timezone…"
                    searchPlaceholder="Search a city or zone…"
                    invalid={Boolean(errors.timezone)}
                    onChange={(value) => setField('timezone', value)}
                  />
                )}
              </Field>
            </div>

            {isEdit && user.reportingCurrency !== undefined && (
              <Field label="Reporting currency" error={errors.reportingCurrency}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Select
                    id={id}
                    aria-describedby={describedBy}
                    name="reportingCurrency"
                    value={form.reportingCurrency}
                    invalid={Boolean(errors.reportingCurrency)}
                    onChange={(event) => setField('reportingCurrency', event.target.value)}
                  >
                    <option value="">None</option>
                    {currencyOptions.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}

            {/*
              Group membership.
              ⚠️ This is ENFORCEMENT, not a label: naming somebody outside the
              Account lead group as a company's or contact's `accountLeadId` is
              a 422 on both endpoints, create and update. So this control is the
              only way to make a person selectable there, and the hint says so
              plainly rather than hedging.
            */}
            <div className="sm:col-span-2">
              <Field
                label="User groups"
                error={errors.groupIds}
                hint={
                  groups.unavailable
                    ? 'Not set up on the API yet.'
                    : 'Decides who can be named as an account lead or client success manager.'
                }
              >
                {({ id, 'aria-describedby': describedBy }) => (
                  <MultiCombobox
                    id={id}
                    aria-describedby={describedBy}
                    values={form.groupIds}
                    options={groupOptions}
                    onChange={(next) => setField('groupIds', next)}
                    loading={groups.loading}
                    // Disabled only when the endpoint is absent: there is
                    // nothing to offer and nothing this form could send. A
                    // FAILED read leaves it enabled, because the account's own
                    // memberships are still injected above and removing one is
                    // the thing that still works.
                    disabled={groups.unavailable}
                    placeholder={
                      groups.unavailable
                        ? 'Not set up on the API yet'
                        : groups.loading
                          ? 'Loading groups…'
                          : 'In no group'
                    }
                    searchPlaceholder="Search groups…"
                    summaryNoun={{ singular: 'group', plural: 'groups' }}
                    // Form-staged: membership is reviewed before Save. The
                    // list-row editor (`InlineUserGroups`) deliberately does
                    // NOT have it — that one writes a whole-set replace on
                    // every single toggle.
                    selectAll
                    invalid={Boolean(errors.groupIds)}
                    invalidValues={refusedGroups}
                  />
                )}
              </Field>
            </div>
          </div>

          {isEdit ? (
            // 2026-10-02: resetting a password is `users.manage-access`.
            canManageAccess && (
            <PasswordReset
              open={resetOpen}
              onToggle={toggleReset}
              isSelf={isSelf}
              name={user.name}
              error={errors.password}
            >
              {passwordInput}
            </PasswordReset>
            )
          ) : (
            /*
             * ⚠️ NOTHING HERE NOW SAYS THE TEMPORARY PASSWORD IS NOT EMAILED.
             * SUPERSEDED, removed 2026-09-05 under the stricter sweep: "Saving
             * does not email it. Sending the onboarding email is a separate
             * step." The `PasswordReset` disclosure below survives by name, but
             * that is the EDIT path; on CREATE an admin who assumes saving tells
             * the new account holder their password leaves them unable to sign
             * in, and no sentence on this screen now says otherwise.
             */
            <Field label="Temporary password" required error={errors.password}>
              {passwordInput}
            </Field>
          )}
        </form>
      </Modal>

      {/*
        The reset confirmation. Nested inside the form modal on purpose — Modal
        stops Escape and Tab at its own panel precisely so a dialog opened from
        another one keeps its own focus trap.
      */}
      <ConfirmDialog
        open={pendingReset !== null}
        onClose={() => setPendingReset(null)}
        onConfirm={() => {
          if (pendingReset) void submit(pendingReset);
        }}
        title={isSelf ? 'Change your password?' : `Reset the password for ${user?.name ?? 'this user'}?`}
        description={
          isSelf ? (
            <>
              Takes effect immediately and signs you out everywhere — you will need the new
              password at your next sign-in. An email confirming the change is sent to you.
            </>
          ) : (
            <>
              Sets a new password for {user?.name ?? 'this user'}
              {user?.email ? ` (${user.email})` : ''} immediately. Their old one stops working, they
              are signed out everywhere, and they are emailed that an administrator reset it. The
              email does not contain the password — pass it on yourself.
            </>
          )
        }
        confirmLabel={isSelf ? 'Change password' : 'Reset password'}
        loading={saving}
      />
    </>
  );
}

/**
 * The admin password reset, behind a disclosure that is closed by default.
 *
 * `PATCH /users/:id` accepts a password with no current-password check, so this
 * must never read as an ordinary field. Closed, it sends nothing; opening it is a
 * deliberate act, and the consequences are stated before the input rather than
 * after the damage.
 *
 * *SUPERSEDED 2026-09-24: "…and no notification to the account holder".* Since
 * then a password set here REVOKES every one of the person's sessions and EMAILS
 * them that an administrator reset it (a self-change is emailed too, worded as a
 * change from their own account). The email never carries the password, which
 * is why the copy still says to pass it on.
 */
function PasswordReset({
  open,
  onToggle,
  isSelf,
  name,
  error,
  children,
}: {
  open: boolean;
  onToggle: (open: boolean) => void;
  isSelf: boolean;
  name: string;
  error?: string;
  children: (props: { id: string; 'aria-describedby': string | undefined }) => React.ReactNode;
}) {
  return (
    <details
      open={open}
      onToggle={(event) => onToggle(event.currentTarget.open)}
      className="rounded-lg border border-border bg-surface"
    >
      <summary
        className={cx(
          'flex min-h-11 cursor-pointer list-none items-center gap-2 rounded-lg px-3 py-2.5 select-none sm:min-h-0',
          'text-sm font-medium text-fg',
          'transition-colors duration-150 ease-out hover:bg-surface-hover motion-reduce:transition-none',
          '[&::-webkit-details-marker]:hidden',
        )}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="h-4 w-4 shrink-0 text-fg-subtle"
        >
          {open ? <path d="m6 9 6 6 6-6" /> : <path d="m9 18 6-6-6-6" />}
        </svg>
        <span className="min-w-0 flex-1 truncate">Set a new password</span>
        <span className="text-2xs text-fg-subtle max-sm:hidden">Optional</span>
      </summary>

      <div className="grid gap-3 border-t border-border px-3 py-3">
        <Notice
          tone="warning"
          title={isSelf ? 'This changes your own password.' : `This resets ${name}’s password.`}
        >
          <div className="min-w-0">
            <ul className="m-0 grid list-disc gap-0.5 pl-4">
              <li>Takes effect as soon as you save.</li>
              <li>
                {isSelf
                  ? 'Your current password is not required.'
                  : 'Their old password stops working.'}
              </li>
              <li>
                {isSelf
                  ? 'Saving signs you out everywhere — you sign back in with the new password.'
                  : 'Saving signs them out everywhere.'}
              </li>
              <li>
                {isSelf
                  ? 'You are emailed that your password was changed.'
                  : 'They are emailed that an administrator reset it. The email does not contain the password — pass it on yourself.'}
              </li>
            </ul>
            <p className="mt-1 mb-0">Leave this closed to send nothing.</p>
          </div>
        </Notice>

        <Field label={isSelf ? 'New password' : `New password for ${name}`} error={error}>
          {children}
        </Field>
      </div>
    </details>
  );
}

function EyeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M10.7 5.1A10.6 10.6 0 0 1 12 5c6.4 0 10 7 10 7a17.6 17.6 0 0 1-2.8 3.8" />
      <path d="M6.6 6.6A17.4 17.4 0 0 0 2 12s3.6 7 10 7a10.4 10.4 0 0 0 5.4-1.5" />
      <path d="M14.1 14.1a3 3 0 1 1-4.2-4.2" />
      <path d="m2 2 20 20" />
    </svg>
  );
}
